import { Component, OnInit, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Destroy } from '@mds/angular-core';
import { LazyLoadEvent } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, combineLatest, of, timer } from 'rxjs';
import { debounce, map, startWith, switchMap, tap } from 'rxjs/operators';
import { Formify } from '../../models/form.model';
import { PaginatedListModel } from '../../models/paginated-list.model';
import { PaginationDefaults } from '../../models/pagination-defaults.model';

@Component({
  template: '',
})
export abstract class BasePagedListComponent<TModel, TSearchModel> extends Destroy implements OnInit {
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly totalRecordsSubject = new BehaviorSubject<number>(10000);
  private readonly reloadSubject = new BehaviorSubject('initial');
  private readonly dataSubject = new BehaviorSubject<TModel[]>([]);
  private readonly lazyLoadSubject = new Subject<LazyLoadEvent>();

  private isOnInit = true;

  readonly isLoading$ = this.isLoadingSubject.asObservable();
  readonly totalRecords$ = this.totalRecordsSubject.asObservable();
  readonly data$: Observable<TModel[]>;
  readonly hasWriteScopes$: Observable<boolean>;
  readonly hasAuditScopes$: Observable<boolean>;
  readonly PaginationDefaults = PaginationDefaults;

  searchForm: FormGroup<Formify<TSearchModel>>;
  first = 0;

  constructor() {
    super();

    const activatedRoute = inject(ActivatedRoute);

    this.data$ = this.dataSubject.asObservable();
    this.hasWriteScopes$ = activatedRoute.data.pipe(map((x) => x.hasWriteScopes as boolean));
    this.hasAuditScopes$ = activatedRoute.data.pipe(map((x) => x.hasAuditScopes as boolean));
  }

  ngOnInit(): void {
    const searchChanges$ = this.searchForm.valueChanges.pipe(
      startWith(undefined),
      tap(() => this.resetPagination())
    );

    combineLatest([this.lazyLoadSubject, searchChanges$, this.reloadSubject])
      .pipe(
        tap(() => this.dataSubject.next([])),
        debounce(() => (this.isOnInit ? timer(0) : timer(300))),
        tap(() => this.isLoadingSubject.next(true)),
        tap(() => (this.isOnInit = false)),
        switchMap(([event]) => this.loadData({ ...event, first: this.first })),
        tap((result) => this.totalRecordsSubject.next(result.totalCount)),
        map((result) => result.items),
        tap(() => this.isLoadingSubject.next(false))
      )
      .subscribe(this.dataSubject);
  }

  reload(): void {
    this.reloadSubject.next('reload');
  }

  lazyLoad(event: LazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.lazyLoadSubject.next(event);
  }

  protected loadData(_: LazyLoadEvent): Observable<PaginatedListModel<TModel>> {
    return of({
      items: [],
      totalCount: 0,
    } as PaginatedListModel<TModel>);
  }

  protected resetView(): void {
    this.resetPagination();
    this.reload();
  }

  private resetPagination(): void {
    this.first = 0;
  }
}
